import {ChangeEvent, FC, useRef} from 'react'
import {TKey, useTranslation} from '@/translations/i18n'
import {Table} from '@/components/commons/table/Table'
import {TableBodyCell, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {TableBodyRow} from '@/components/ui/table-body-row/TableBodyRow'
import {TableInputSearch} from '@/components/commons/table-input-search/TableInputSearch'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Button} from '@/components/ui/button/Button'
import {Badge, BadgeProps} from '@/components/ui/badge/Badge'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {formatDisplayDateTime} from '@/dayjs'
import {debounce} from '@/utilities/helpers'
import {useMailBatches} from '@/features/mailer/queries/useMailBatches'
import {useMailHistorySearchParams} from '@/features/mailer/hooks/useMailHistorySearchParams'
import {MailBatchSummary} from '@/features/mailer/types'
import {
    StyledCountsCluster,
    StyledMailerHistoryErrorBox,
    StyledMailerHistoryTab,
    StyledMailerHistoryToolbar,
    StyledTriggeredByCell
} from './style'

const COLUMN_IDS = {
    subject: 'subject',
    triggeredBy: 'triggeredBy',
    totalRecipients: 'totalRecipients',
    counts: 'counts',
    createdAt: 'createdAt'
} as const

type ColumnId = (typeof COLUMN_IDS)[keyof typeof COLUMN_IDS]

export const MailerHistoryTab: FC = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useMailHistorySearchParams()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)

    const batchesQuery = useMailBatches(
        {
            page: searchParams.page,
            limit: searchParams.limit,
            search: searchParams.search
        },
        {enabled: searchParams.view === 'history'}
    )

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 500)

    const openBatch = (batchId: string) => {
        setSearchParams({batch: batchId, detailPage: 1, detailStatus: undefined})
    }

    return (
        <StyledMailerHistoryTab>
            <StyledMailerHistoryToolbar>
                <TableInputSearch
                    ref={inputSearchRef}
                    defaultValue={searchParams.search}
                    onChange={onSearch}
                    placeholder={t('mailer:history_search_placeholder')}
                />
            </StyledMailerHistoryToolbar>

            {batchesQuery.isError ? (
                <StyledMailerHistoryErrorBox>{t('errors:default')}</StyledMailerHistoryErrorBox>
            ) : (
                <Table<MailBatchSummary[], ColumnId>
                    emptyStateComponent={
                        <EmptyState
                            title={t('mailer:history_empty_title')}
                            subtitle={t('mailer:history_empty_description')}
                        >
                            {searchParams.search && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchParams({search: undefined, page: 1})
                                        if (inputSearchRef.current) {
                                            inputSearchRef.current.value = ''
                                        }
                                    }}
                                >
                                    {t('mailer:history_clear_search')}
                                </Button>
                            )}
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            key={index}
                            onClick={dataItem ? () => openBatch(dataItem.batchId) : undefined}
                            style={{cursor: dataItem ? 'pointer' : 'default'}}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...batchesQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => setSearchParams({limit, page: 1})
                    }}
                    isLoading={batchesQuery.isPending}
                    data={batchesQuery.data?.data ?? []}
                    columns={[
                        {
                            id: COLUMN_IDS.subject,
                            header: () => t('mailer:history_column_subject'),
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText variant="dark" title={dataItem.subject}>
                                        {dataItem.subject}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            minSize: () => 200,
                            size: () => 320,
                            maxSize: () => 480
                        },
                        {
                            id: COLUMN_IDS.triggeredBy,
                            header: () => t('mailer:history_column_triggered_by'),
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.triggeredBy ? (
                                        <StyledTriggeredByCell>
                                            {dataItem.triggeredBy.firstName} {dataItem.triggeredBy.lastName}
                                        </StyledTriggeredByCell>
                                    ) : (
                                        <StyledTriggeredByCell className="unknown">
                                            {t('mailer:history_triggered_by_unknown')}
                                        </StyledTriggeredByCell>
                                    )}
                                </TableBodyCell>
                            ),
                            size: () => 180
                        },
                        {
                            id: COLUMN_IDS.totalRecipients,
                            header: () => t('mailer:history_column_recipients'),
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.totalRecipients}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            size: () => 110
                        },
                        {
                            id: COLUMN_IDS.counts,
                            header: () => t('mailer:history_column_counts'),
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <CountsCluster counts={dataItem.counts} />
                                </TableBodyCell>
                            ),
                            size: () => 240
                        },
                        {
                            id: COLUMN_IDS.createdAt,
                            header: () => t('mailer:history_column_created_at'),
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatDisplayDateTime(dataItem.createdAt)}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            size: () => 170
                        }
                    ]}
                />
            )}
        </StyledMailerHistoryTab>
    )
}

interface CountsClusterProps {
    counts: MailBatchSummary['counts']
}

type CountKey = keyof MailBatchSummary['counts']

const COUNT_VARIANTS: Array<{key: CountKey; variant: NonNullable<BadgeProps['variant']>; labelKey: TKey}> = [
    {key: 'sent', variant: 'success', labelKey: 'mailer:history_counts_sent'},
    {key: 'failed', variant: 'danger', labelKey: 'mailer:history_counts_failed'},
    {key: 'bounced', variant: 'orange', labelKey: 'mailer:history_counts_bounced'},
    {key: 'pending', variant: 'neutral', labelKey: 'mailer:history_counts_pending'}
]

const CountsCluster: FC<CountsClusterProps> = ({counts}) => {
    const {t} = useTranslation()
    const hasAny = COUNT_VARIANTS.some(({key}) => counts[key] > 0)

    return (
        <StyledCountsCluster>
            {COUNT_VARIANTS.map(({key, variant, labelKey}) =>
                counts[key] > 0 ? (
                    <Tooltip key={key} content={t(labelKey, {count: counts[key]})} variant="light">
                        <Badge variant={variant} size="sm">
                            {counts[key]}
                        </Badge>
                    </Tooltip>
                ) : null
            )}
            {!hasAny && (
                <Badge variant="neutral" size="sm">
                    0
                </Badge>
            )}
        </StyledCountsCluster>
    )
}
